import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Calendar, MapPin, Users, Plane } from "lucide-react";
import PageSEO from "@/components/PageSEO";

export default function RetreatDetail() {
  const { id } = useParams();
  const [retreat, setRetreat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", intentions: "", dietary: "", medical: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/retreats").then(r => {
      const found = (r.data || []).find(x => x.id === id);
      setRetreat(found || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/retreats/apply", { ...form, retreat_id: retreat.id, retreat_title: retreat.title });
      toast.success("Application received. We'll be in touch within 3 days.");
      setForm({ name: "", email: "", phone: "", intentions: "", dietary: "", medical: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-32 text-ink-soft">Loading retreat…</div>;
  if (!retreat) return (
    <div className="max-w-4xl mx-auto px-6 py-32 text-center">
      <PageSEO
        title={"Retreat | Celestial Seed Sanctuary"}
        description={"Apply for a small-group retreat at Celestial Seed Sanctuary in the cloud forests of Cundinamarca, Colombia."}
      />
      <h1 className="font-serif text-3xl mb-4">Retreat not found</h1>
      <Link to="/retreats" className="pill pill-primary">Back to Retreats</Link>
    </div>
  );

  return (
    <div>
      <section className="relative h-[60vh] overflow-hidden">
        <img src={retreat.image_url} alt={retreat.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/40" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 w-full text-bone">
            <Link to="/retreats" data-testid="retreat-back" className="inline-flex items-center gap-2 text-bone/85 hover:text-bone text-sm mb-6">
              <ArrowLeft className="h-4 w-4" /> All Retreats
            </Link>
            <div className="overline text-bone/80 mb-3">{dayjs(retreat.start_date).format("MMM D")} — {dayjs(retreat.end_date).format("MMM D, YYYY")}</div>
            <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl" data-testid="retreat-detail-title">{retreat.title}</h1>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="overline mb-3">About this Retreat</div>
            <p className="text-ink text-lg leading-relaxed" data-testid="retreat-description">{retreat.description}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 pt-6">
            <div className="border border-line rounded-2xl p-6 bg-surface">
              <Calendar className="h-5 w-5 text-terracotta mb-3" strokeWidth={1.5} />
              <div className="overline">Dates</div>
              <div className="mt-1">{dayjs(retreat.start_date).format("MMM D")} — {dayjs(retreat.end_date).format("MMM D, YYYY")}</div>
            </div>
            <div className="border border-line rounded-2xl p-6 bg-surface">
              <MapPin className="h-5 w-5 text-terracotta mb-3" strokeWidth={1.5} />
              <div className="overline">Location</div>
              <div className="mt-1">Celestial Seed Sanctuary, Cundinamarca, Colombia</div>
            </div>
            <div className="border border-line rounded-2xl p-6 bg-surface">
              <Plane className="h-5 w-5 text-terracotta mb-3" strokeWidth={1.5} />
              <div className="overline">Getting Here</div>
              <div className="mt-1 text-sm text-ink-soft">Fly into Bogotá (BOG). Collective ground transport arranged from Bogotá (~2 hrs).</div>
            </div>
            <div className="border border-line rounded-2xl p-6 bg-surface">
              <Users className="h-5 w-5 text-terracotta mb-3" strokeWidth={1.5} />
              <div className="overline">Format</div>
              <div className="mt-1 text-sm text-ink-soft">Small group. Application only. Long integration.</div>
            </div>
          </div>

          <div className="pt-6">
            <div className="overline mb-3">Before you apply</div>
            <ul className="space-y-2 text-ink-soft">
              <li>• Cloud-forest climate — cool mornings, mild afternoons, frequent light rain</li>
              <li>• Elevation approximately 1,800 m (6,000 ft) — allow time to acclimate</li>
              <li>• Sturdy shoes, layers, rain jacket, water bottle, journal</li>
              <li>• Full medical intake before your arrival</li>
            </ul>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-line rounded-2xl p-8 bg-surface sticky top-24">
            <div className="overline">Investment</div>
            <div className="font-serif text-4xl mt-1">${retreat.price}<span className="text-ink-soft text-sm"> / person</span></div>
            <div className="text-ink-soft text-xs mt-1">Non-refundable deposit secures your place.</div>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <div><Label className="overline">Full name</Label><Input required data-testid="detail-apply-name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} /></div>
              <div><Label className="overline">Email</Label><Input type="email" required data-testid="detail-apply-email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} /></div>
              <div><Label className="overline">Phone</Label><Input data-testid="detail-apply-phone" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} /></div>
              <div><Label className="overline">Your intentions</Label><Textarea rows={3} data-testid="detail-apply-intentions" value={form.intentions} onChange={e=>setForm({...form, intentions: e.target.value})} /></div>
              <div><Label className="overline">Dietary needs</Label><Input data-testid="detail-apply-dietary" value={form.dietary} onChange={e=>setForm({...form, dietary: e.target.value})} /></div>
              <div><Label className="overline">Medical notes</Label><Input data-testid="detail-apply-medical" value={form.medical} onChange={e=>setForm({...form, medical: e.target.value})} /></div>
              <Button type="submit" disabled={busy} data-testid="detail-apply-submit" className="w-full bg-forest text-bone hover:bg-ink rounded-full py-6">
                {busy ? "Sending..." : "Apply for this Retreat"}
              </Button>
            </form>
          </div>
        </aside>
      </section>
    </div>
  );
}
